// 消費税率。現状は内税として定義。
type TaxRate = {
  rate: number;
  stripeId: string;
};

// 税率の stripeId を取得するための環境変数
// バックエンドでのみ必要なため、とりあえずバックエンドのみ正確に判定
const appEnv =
  // eslint-disable-next-line no-restricted-syntax
  typeof process !== "undefined" && process.env ? process.env["APP_ENV"] : null;
const isProduction = appEnv === "prd";

export const taxMaster = {
  標準税率: {
    rate: 0.1,
    stripeId: isProduction
      ? "txr_1JuSB8AOT8RkAcwmo84X49hA"
      : "txr_1Pg4gEAOT8RkAcwmdYxRl0vp",
  },
  軽減税率: {
    rate: 0.08,
    stripeId: isProduction
      ? "txr_1Pg4hbAOT8RkAcwmX4vIevMU"
      : "txr_1Pg4h1AOT8RkAcwmHCqqhs0z",
  },
  非課税: {
    rate: 0,
    stripeId: isProduction
      ? "txr_1R21WUAOT8RkAcwmi2r0jZ4s"
      : "txr_1R21XWAOT8RkAcwmX8FdU7LH",
  },
} as const;

export type TaxRateKey = keyof typeof taxMaster;

const { 標準税率, 軽減税率: _軽減税率, 非課税 } = taxMaster;

// 料金明細の項目に表示される項目名
export const priceLabels = {
  cleaning_fee_original: "清掃費",
  cleaning_fee_simple: "簡易清掃費",
  peak_day_original: "繁忙日",
  friday_sunday_original: "金曜・日曜",
  sauna_use_original: "サウナ利用",
  onsen_use_original: "天然温泉利用",
  ev_charger_use_original: "EV充電器利用",
  bring_dog_original: "愛犬同伴",
  dog_run_use_original: "ドッグラン利用",
  additional_after_eighth_night_original: "8泊目以降追加料金",
  base_on_admin_original: "管理者予約用基本料金",
  // TODO: ラベルは暫定
  large_accommodation_original: "Large サイズ追加料金",
  // 以下は price では定義されず、別のところから計算される料金種別
  calculated_base_fee: "基本料金",
  calculated_architecture_type_additional_fee: "施設追加料金",
  calculated_class_additional_fee: "クラス追加料金",
  calculated_site_additional_fee: "拠点追加料金",
  calculated_cancellation_fee: "キャンセル料金",
  calculated_dynamic_pricing_fee: "ダイナミックプライシング料金",
  calculated_early_check_in_fee: "アーリーチェックイン料金",
  // 地方税は実際の明細には自治体名も含まれる、e.g. "宿泊税（白馬村）"
  calculated_accommodation_tax: "宿泊税",
  calculated_bathing_tax: "入湯税",
  // 規定人数を超えた場合の人数追加料金
  calculated_additional_guest_fee: "人数追加料金",
  customize_accommodation_fee: "宿泊料金",
} as const;

export type PriceKey = keyof typeof priceLabels;

export const optionPriceKeys: PriceKey[] = [
  "bring_dog_original",
  "ev_charger_use_original",
];

export const additionalGuestPriceKeys: PriceKey[] = [
  "calculated_additional_guest_fee",
];

export const localTaxPriceKeys = [
  "calculated_accommodation_tax",
  "calculated_bathing_tax",
] as const satisfies PriceKey[];

type CalculatedPriceKey =
  | "calculated_base_fee"
  | "calculated_architecture_type_additional_fee"
  | "calculated_class_additional_fee"
  | "calculated_site_additional_fee"
  | "calculated_cancellation_fee"
  | "calculated_dynamic_pricing_fee"
  | "calculated_early_check_in_fee"
  | "calculated_accommodation_tax"
  | "calculated_bathing_tax"
  | "calculated_additional_guest_fee";

export type FixedPriceKey = Exclude<
  PriceKey,
  CalculatedPriceKey | "customize_accommodation_fee"
>;

export const priceTaxRates: Record<PriceKey, TaxRate | null> = {
  cleaning_fee_original: 標準税率,
  cleaning_fee_simple: 標準税率,
  peak_day_original: 標準税率,
  friday_sunday_original: 標準税率,
  sauna_use_original: 標準税率,
  onsen_use_original: 標準税率,
  ev_charger_use_original: 標準税率,
  bring_dog_original: 標準税率,
  dog_run_use_original: 標準税率,
  additional_after_eighth_night_original: 標準税率,
  base_on_admin_original: 標準税率,
  large_accommodation_original: 標準税率,
  calculated_base_fee: 標準税率,
  calculated_architecture_type_additional_fee: 標準税率,
  calculated_class_additional_fee: 標準税率,
  calculated_site_additional_fee: 標準税率,
  calculated_cancellation_fee: 非課税,
  calculated_dynamic_pricing_fee: 標準税率,
  calculated_early_check_in_fee: 標準税率,
  calculated_accommodation_tax: 非課税,
  calculated_bathing_tax: 非課税,
  calculated_additional_guest_fee: 標準税率,
  customize_accommodation_fee: 標準税率,
};

export const STANDARD_CANCELLATION_FEE = 5500;
// Weekdayプラン新ルール用（4/25以降のfirstNightに適用）
export const WEEKDAY_CANCELLATION_FEE = 11000;
// forBiz 2027年度ルール改定用（改定後の宿泊日に適用）
export const BUSINESS_PLAN_CANCELLATION_FEE = 11000;

export const priceAmounts: Record<FixedPriceKey, number> = {
  cleaning_fee_original: 3300,
  cleaning_fee_simple: 3300,
  peak_day_original: 5500,
  friday_sunday_original: 5500,
  sauna_use_original: 5500,
  onsen_use_original: 5500,
  ev_charger_use_original: 1650,
  bring_dog_original: 3300,
  dog_run_use_original: 3300,
  additional_after_eighth_night_original: 33000,
  base_on_admin_original: 33000,
  large_accommodation_original: 5500,
};

// https://www.notion.so/sanu/Stay-Gift-One-more-Cabin-17dd14f248a8806f80c4c4e38ceb21cd?pvs=4#17dd14f248a881b6afb3c00aadac3d4c
export const STAY_GIFT_PRICE = 28000;

const oneMoreCabinFeeForNight: Record<number, number> = {
  1: 28000, // 28,000
  2: 25000, // 53,000
  3: 24000, // 77,000
  4: 22000, // 99,000
};
export const ONE_MORE_CABIN_MAX_NIGHTS = Object.keys(
  oneMoreCabinFeeForNight
).length;

type PriceItem = {
  key: PriceKey;
  amount: number;
  label: string;
  taxRate: number;
  stripeTaxRateId: string | null;
};

type PriceItemWithoutAmount = Omit<PriceItem, "amount">;

export function getPriceItem(key: FixedPriceKey): PriceItem {
  const taxRate = priceTaxRates[key];
  return {
    key,
    amount: priceAmounts[key],
    label: priceLabels[key],
    taxRate: taxRate === null ? 0 : taxRate.rate,
    stripeTaxRateId: taxRate === null ? null : taxRate.stripeId,
  };
}

export function getPriceItemWithoutAmount(
  key: CalculatedPriceKey
): PriceItemWithoutAmount {
  const taxRate = priceTaxRates[key];
  return {
    key,
    label: priceLabels[key],
    taxRate: taxRate === null ? 0 : taxRate.rate,
    stripeTaxRateId: taxRate === null ? null : taxRate.stripeId,
  };
}

export function getPriceItemForAdmin(
  key: string
): PriceItemWithoutAmount & { amount?: number | undefined } {
  if (!(key in priceLabels)) {
    throw new Error(`Invalid price key: ${key}`);
  }
  const priceKey = key as PriceKey;
  const hasAmount = priceKey in priceAmounts;
  if (hasAmount) {
    return getPriceItem(priceKey as FixedPriceKey);
  } else {
    return getPriceItemWithoutAmount(priceKey as CalculatedPriceKey);
  }
}

export function getOneMoreCabinFeeForNight(nightNumber: number): number {
  if (nightNumber <= 0 || nightNumber > 4) {
    throw new Error(
      "OneMoreCabin reservation for more than 4 nights is not supported"
    );
  }
  return oneMoreCabinFeeForNight[nightNumber]!;
}

export type UnitCharacter = "¥" | "円" | "JPY";

/**
 * 任意の string を UnitCharacter literal に変換する。
 * i18n 経由で取得した string を formatCurrency に渡す際に使用。
 */
export function toUnitCharacter(value: string): UnitCharacter {
  if (value === "円") return "円";
  if (value === "JPY") return "JPY";
  return "¥";
}

// 3桁ごとにカンマを入れる、単位をつける
export function formatCurrency(
  amount: number,
  opts: { excludeSeparator?: boolean; unitCharacter?: UnitCharacter } = {}
): string {
  const unitCharacter = opts.unitCharacter ?? "¥";
  const formatted = opts.excludeSeparator
    ? `${amount}`
    : amount.toLocaleString("ja-JP");
  if (unitCharacter === "JPY") return `${formatted} JPY`;
  return [
    unitCharacter === "¥" ? unitCharacter : "",
    formatted,
    unitCharacter === "円" ? unitCharacter : "",
  ].join("");
}
